<template>
  <div>
    <date-range-picker
      v-model="dateRangeFilter"
      :min-date="minDate"
      :max-date="maxDate"
      :single-date-picker="singleDatePicker"
      v-bind="pickerConfig"
      :locale-data="localeData"
      :ranges="computedRanges"
      @select="handleSelect"
      @update="handleUpdate"
      class="date-range-picker"
    >
      <template #input="picker">
        <div class="flex items-center justify-between gap-2">
          <span class="date-range-text">
            {{ dateRangeFilter.range }}
            ({{ formatDateFilterRange(picker.startDate, picker.endDate) }})
          </span>
          <img src="../../assets/dropdown.svg" alt="" />
        </div>
      </template>

      <template #date="{ date }">
        <span class="date-cell">{{ formatDateCell(date) }}</span>
      </template>

      <div slot="footer" slot-scope="data" class="slot">
        <div class="footer-actions">
          <div class="footer-actions-buttons">
            <button
              class="button-gemnote button-gemnote--small"
              @click="data.clickApply"
            >
              <span>Set</span>
            </button>
            <button
              @click="
                () => {
                  data.clickCancel(), $emit('onCancel')
                }
              "
              class="button-gemnote-light button-gemnote--small"
            >
              <span>Cancel</span>
            </button>
          </div>
        </div>
      </div>
    </date-range-picker>
  </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import Util from '@/mixins/Util'

export default {
  name: 'CustomDateRangePicker',
  mixins: [Util],
  components: {
    DateRangePicker
  },

  props: {
    minDate: {
      type: Date,
      default: null
    },
    maxDate: {
      type: Date,
      default: null
    },
    singleDatePicker: {
      type: [Boolean, String],
      default: 'range'
    },
    opens: {
      type: String,
      default: 'center',
      validator: value => ['left', 'right', 'center'].includes(value)
    },
    timePicker: {
      type: Boolean,
      default: false
    },
    timePicker24Hour: {
      type: Boolean,
      default: true
    },
    showWeekNumbers: {
      type: Boolean,
      default: false
    },
    showDropdowns: {
      type: Boolean,
      default: false
    },
    autoApply: {
      type: Boolean,
      default: false
    },
    linkedCalendars: {
      type: Boolean,
      default: true
    },
    dateFormat: {
      type: String,
      default: 'dd-MM-yyyy'
    },
    dateRange: {
      type: Object,
      default: () => ({
        startDate: new Date().setHours(0, 0, 0, 0),
        endDate: new Date().setHours(0, 0, 0, 0),
        range: 'Today'
      })
    }
  },

  created() {
    this.dateRangeFilter = this.dateRange
  },

  watch: {
    dateRange: {
      immediate: true,
      handler(newValue) {
        this.dateRangeFilter = newValue
      }
    }
  },

  data() {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return {
      dateRangeFilter: {},
      localeData: {
        direction: 'ltr',
        format: this.dateFormat,
        separator: ' - ',
        applyLabel: 'Set',
        cancelLabel: 'Cancel',
        weekLabel: 'W',
        daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        monthNames: [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ],
        firstDay: 1
      }
    }
  },

  computed: {
    pickerConfig() {
      return {
        opens: this.opens,
        timePicker: this.timePicker,
        timePicker24Hour: this.timePicker24Hour,
        showWeekNumbers: this.showWeekNumbers,
        showDropdowns: this.showDropdowns,
        autoApply: this.autoApply,
        linkedCalendars: this.linkedCalendars
      }
    },

    computedRanges() {
      const today = new Date()
      return this.getDefaultRanges(today)
    }
  },

  methods: {
    getDefaultRanges(today) {
      const todayStart = this.formatToUTCMidnight(today)
      const todayEnd = this.formatToUTCMidnight(today)
      const yesterday = this.formatToUTCMidnight(
        new Date(today.setDate(today.getDate() - 1))
      )
      const weekStart = this.formatToUTCMidnight(
        new Date(today.setDate(today.getDate() - 6))
      )

      return {
        Today: [todayStart, todayEnd],
        Yesterday: [yesterday, yesterday],
        'Last 7 days': [weekStart, todayEnd],
        'Last 30 days': [
          this.formatToUTCMidnight(
            new Date(today.setDate(today.getDate() - 30))
          ),
          yesterday
        ]
      }
    },

    formatDateCell(date) {
      return date.getDate()
    },

    handleSelect(date) {
      const selectedRange = this.findSelectedRange(date) || ''
      this.dateRangeFilter = {
        startDate: date.startDate,
        endDate: date.endDate,
        range: selectedRange
      }
    },
    handleUpdate(date) {
      const selectedRange = this.findSelectedRange(date) || ''
      this.$emit('update:dateRange', {
        startDate: date.startDate,
        endDate: date.endDate,
        range: selectedRange
      })
    },

    findSelectedRange(date) {
      const foundRange = Object.entries(this.computedRanges).find(
        ([rangeName, [rangeStart, rangeEnd]]) => {
          console.log(rangeName, 'rangeName')

          const normalizedStart = this.formatToUTCMidnight(
            date.startDate
          ).getTime()
          const normalizedEnd = this.formatToUTCMidnight(date.endDate).getTime()
          const rangeStartTime = this.formatToUTCMidnight(rangeStart).getTime()
          const rangeEndTime = this.formatToUTCMidnight(rangeEnd).getTime()

          return (
            normalizedStart === rangeStartTime && normalizedEnd === rangeEndTime
          )
        }
      )

      return foundRange ? foundRange[0] : undefined
    }
  }
}
</script>

<style>
@import url('https://cdn.jsdelivr.net/npm/daterangepicker/daterangepicker.css');

.reportrange-text {
  background: transparent !important;
  border: none !important;
  font-size: 14px !important;
  color: #232220 !important;
}

.reportrange-text {
  min-width: 340px;
  max-width: 350px;
  width: 300px;
  text-align: left;
  padding-bottom: 0 !important;
}

@media screen and (min-width: 339px) {
  .daterangepicker.single.show-ranges.show-weeknumbers,
  .daterangepicker.single.show-ranges {
    min-width: 550px !important;
    right: 0 !important;
    left: 19% !important;
  }
}

.daterangepicker.show-ranges.single.ltr .drp-calendar.left {
  border-left: none !important;
}
.daterangepicker {
  background: #f5f2ee !important;
  border: 1px solid #232220 !important;
  border-radius: 0px !important;
}

.calendars {
  flex-direction: row-reverse !important;
  justify-content: flex-end !important;
  flex-wrap: nowrap !important;
}

.calendars-container {
  width: 75% !important;
  border-right: 1px solid #232220 !important;
  padding: 0.5rem;
}
.daterangepicker.show-ranges .ranges {
  width: 25% !important;
  margin-top: 3rem;
  padding: 1rem 0;
}
.daterangepicker .ranges li.active {
  background: #dfdad2 !important;
  color: #232220 !important;
}
.daterangepicker .ranges li:hover {
  background: #dfdad230 !important;
}

.daterangepicker.show-ranges .ranges li {
  color: #232220;
  font-size: 14px;
  padding: 0px 1.5rem !important;
}

.drp-calendar {
  min-width: 100% !important;
  min-height: 100% !important;
}

.calendar-table {
  background: #f5f2ee !important;
}
.table-condensed {
  background: #f5f2ee !important;
}

.daterangepicker .calendar-table thead th {
  padding-bottom: 1rem;
}
.daterangepicker .calendar-table tbody th {
  font-weight: 400;
  font-size: 14px !important;
}
.daterangepicker .calendar-table th,
.daterangepicker .calendar-table td {
  background: transparent !important;
  font-size: 14px !important;
  font-family: 'Inter';
  min-width: 40px;
  min-height: 40px;
  height: 40px !important;
  padding: 10px;
}

.daterangepicker td.off {
  color: #a09c95 !important;
  opacity: 0.4;
  font-weight: 400 !important;
}
.daterangepicker td.in-range {
  background: #dfdad2 !important;
}

.daterangepicker td.active,
.daterangepicker td.active:hover {
  background: #232220 !important;
  /* border-radius: 50% !important; */
}

.daterangepicker.openscenter::after,
.daterangepicker.openscenter:before {
  display: none !important;
}

.daterangepicker th.month {
  font-size: 16px !important;
  font-weight: 400 !important;
}

.drp-buttons {
  display: none !important;
}
.footer-actions {
  margin-left: auto;
  position: absolute;
  bottom: 0;
  width: 25%;
  background: transparent;
  right: 0;
  padding: 2rem 1.5rem !important;
  padding-bottom: 1rem !important;
}
.footer-actions-buttons {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  z-index: 10;
}

.button-gemnote-light,
.button-gemnote {
  cursor: pointer;
  z-index: 10;
}

.button-gemnote-light {
  width: 100%;
  background: #f5f2ee;
  color: #24292c;
  padding: 0 1.25rem;
  height: 2.5rem;
  border-radius: 40px;
  text-transform: uppercase;
  font-family: 'Inter', sans-serif;

  &:hover {
    background: #f5f2ee;
    color: #24292c;
  }
}

.button-gemnote {
  background: #24292c;
  color: #f5f2ee;
  padding: 0 1.25rem;
  height: 2.5rem;
  border-radius: 40px;
  text-transform: uppercase;
  font-family: 'Inter', sans-serif;
  width: 100%;

  &:hover {
    background: #24292c;
    color: #f5f2ee;
  }
}

.button-gemnote--disabled {
  pointer-events: none;
  opacity: 0.5;
}
</style>
